/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useState, useEffect, Fragment, useMemo } from "react";
import { TextField, Stack, Select, MenuItem, FormControl, Checkbox, FormControlLabel } from "@mui/material";
import { useUpdateComponent } from "cms/components/hooks/use-update-component";
import LabelledField from "components/labelled-field";
import { LinkSelectorWrapper } from "cms/links/link-selector/link-selector-wrapper";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { useAuth } from "cms/auth/hooks/use-auth";
import { AddLinkButton } from "./add-link-button";
import { ReactComponent as PlusRegularIcon } from "assets/images/icons/common/plus-regular.svg";
import { ReactComponent as PlusRegularSmallIcon } from "assets/images/icons/common/plus-regular-sm.svg";
import { useSortableComponentList } from "cms/components/hooks/use-sortable-component-list";
import { NewsItemSelectDialog } from "./news-item-select-dialog";
import { FeaturedPageItem } from "./featured-page-item";
import { selectAllSchemas } from "data/store/reducers/schema-model";
import { useSelector } from "react-redux";

/** Default heading text for the news highlights section */
const DEFAULT_HEADING = "Latest news";

/** Default sub-heading text for the recent news feed */
const DEFAULT_SUB_HEADING = "Recent";

/** Default heading text for the featured news section */
const DEFAULT_FEATURED_NEWS_HEADING = "Featured";

/** Model type to search for category enum field options */
const NEWS_SUBTYPE_MODEL = "model.news-subtype";

const defaultFeaturedPage = Object.freeze({
    webpage: "",
});

const NewsHighlightsComponentEditor = () => {
    const { component, update } = useUpdateComponent();
    const { token } = useAuth();

    // Get all schemas and current model type to find field schemas
    const allSchemas = useSelector(selectAllSchemas);

    const categoryOptions = useMemo(() => {
        if (!allSchemas || !Array.isArray(allSchemas)) {
            return null;
        }
        const currentSchema = allSchemas.find(
            (schema) => schema.type === NEWS_SUBTYPE_MODEL
        );

        const options = currentSchema?.fields.find((field) => field.name === "category")?.settings?.options?.select || [];
        
        return options.map((value) => ({
            value: value,
            label: value,
        }));    
    }, [allSchemas]);

    const heading = component?.heading || DEFAULT_HEADING;
    const subHeading = component?.sub_heading || DEFAULT_SUB_HEADING;
    const featuredNewsHeading = component?.featured_news_heading || DEFAULT_FEATURED_NEWS_HEADING;
    const allNewsLinkGuid = component?.all_news_link;
    const allFeaturedPages = component?.featured_pages || [];
    const category = component?.category || "";
    const showDescription = component?.show_description || false;

    const onChangeHeading = (event) => {
        const heading = event.target.value;
        update("heading", heading);
    };

    const onChangeSubHeading = (event) => {
        const subHeading = event.target.value;
        update("sub_heading", subHeading);
    };

    const onChangeFeaturedNewsHeading = (event) => {
        const featuredNewsHeading = event.target.value;
        update("featured_news_heading", featuredNewsHeading);
    };

    const [allNewsLink, setAllNewsLink] = useState();
    const [loadingAllNewsLink, setLoadingAllNewsLink] = useState(false);
    const [isFeaturedNewsActive, setIsFeaturedNewsActive] = useState(allFeaturedPages.length > 0);

    const onChangeFeaturedPages = (pages) => {
        update("featured_pages", pages);
    };

    const {
        list: featuredPagesList,
        sortableMethods,
        controls,
    } = useSortableComponentList(
        allFeaturedPages,
        defaultFeaturedPage,
        onChangeFeaturedPages
    );

    const { addNewItem, removeItem, updateItems } = controls;

    // Set default values on initial render
    useEffect(() => {
        if (!component?.heading) {
            update("heading", DEFAULT_HEADING);
        }
        if (!component?.sub_heading) {
            update("sub_heading", DEFAULT_SUB_HEADING);
        }
        if (!component?.featured_news_heading) {
            update("featured_news_heading", DEFAULT_FEATURED_NEWS_HEADING);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load link object from guid if present
    useEffect(() => {
        const load = async () => {
            if (allNewsLinkGuid && typeof allNewsLinkGuid === "string" && allNewsLinkGuid.length > 20) {
                setLoadingAllNewsLink(true);
                try {
                    const linkObj = await SimpliCityAdminSDK.link.findOne(token, allNewsLinkGuid);
                    setAllNewsLink(linkObj);
                } catch (e) {
                    setAllNewsLink(undefined);
                } finally {
                    setLoadingAllNewsLink(false);
                }
            } else {
                setAllNewsLink(undefined);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allNewsLinkGuid, token]);

    const onChangeAllNewsLink = (link) => {
        setAllNewsLink(link);
        update("all_news_link", link?.guid);
    };

    const onRemoveAllNewsLink = () => {
        setAllNewsLink(undefined);
        update("all_news_link", undefined);
    };

    const onChangeCategory = (event) => {
        update("category", event.target.value);
    };

    const onChangeShowDescription = (event) => {
        update("show_description", event.target.checked);
    };

    const handleFeaturedNewsClick = () => {
        setIsFeaturedNewsActive(true);
        // If list is empty, add one empty item so user can select
        if (featuredPagesList.length === 0) {
            addNewItem();
        }
    };

    const [addDialogOpen, setAddDialogOpen] = useState(false);

    const handleAddFeaturedNewsClick = () => {
        // Limit to 2 items
        if (featuredPagesList.length >= 2) {
            return;
        }
        // If list already has items, open dialog to add new item
        // Otherwise, the empty item from handleFeaturedNewsClick will show the select button
        if (featuredPagesList.length > 0) {
            setAddDialogOpen(true);
        }
    };

    const handleAddToFeaturedPages = (webpage) => {
        if (webpage && webpage.guid) {
            // Limit to 2 items
            if (featuredPagesList.length >= 2) {
                setAddDialogOpen(false);
                return;
            }
            const newPage = {
                webpage: webpage.guid,
            };
            // If list is empty, activate the feature
            if (featuredPagesList.length === 0) {
                setIsFeaturedNewsActive(true);
            }
            const nextPages = [...featuredPagesList, newPage];
            updateItems(nextPages);
            setAddDialogOpen(false);
        }
    };

    return (
        <div className='simplicity-editor-frame news-highlights-editor-frame'>
            <div className="news-highlights-editor-container">
                <div className="news-highlights-editor-row-heading-wrapper">
                    <div className="news-highlights-editor-row-heading-content">
                        <div className="news-highlights-editor-column">
                            <LabelledField label="Heading: Section">
                                <TextField
                                    onChange={onChangeHeading}
                                    id="heading"
                                    defaultValue={heading}
                                    className="news-highlights-text-field"
                                />
                            </LabelledField>
                        </div>
                        {isFeaturedNewsActive && 
                        <div className="news-highlights-editor-column">
                            <LabelledField label="Sub-heading: Recent news feed">
                                <TextField
                                    onChange={onChangeSubHeading}
                                    id="sub_heading"
                                    defaultValue={subHeading}
                                    className="news-highlights-text-field"
                                />
                            </LabelledField>
                        </div>}
                        
                    </div>
                    <div className="news-highlights-editor-row-all-news">
                        <LabelledField label="Link: All news">
                            <div className='link-selector-container'>
                                <LinkSelectorWrapper
                                    link={allNewsLink}
                                    setLink={setAllNewsLink}
                                    onLinkChange={onChangeAllNewsLink}
                                    onLinkRemoved={onRemoveAllNewsLink}
                                    disabled={false}
                                    loadingLink={loadingAllNewsLink}
                                    setLoadingLink={setLoadingAllNewsLink}
                                    AddComponent={AddLinkButton}
                                />
                            </div>
                        </LabelledField>
                    </div>
                    <div className="news-highlights-editor-row-all-news">
                        <LabelledField label="Category">
                            <FormControl fullWidth>
                                <Select
                                    value={category}
                                    onChange={onChangeCategory}
                                    displayEmpty
                                    className="news-highlights-text-field"
                                >
                                    <MenuItem value="">
                                        <em>All</em>
                                    </MenuItem>
                                    {categoryOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </LabelledField>
                    </div>
                    <div className="news-highlights-editor-row-all-news">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={showDescription}
                                    onChange={onChangeShowDescription}
                                />
                            }
                            label="Show description"
                        />
                    </div>
                </div>
                <div className="news-highlights-editor-row-featurednews-selector-wrapper">
                    <div className="featured-news-selector-title-wrapper">
                        <div className="featured-news-selector-title">
                            <div className="featured-news-selector-title-text">Featured news items</div>
                            {isFeaturedNewsActive && featuredPagesList.length < 2 &&
                            <>
                                <div className="featured-news-selector-title-add-container" onClick={handleAddFeaturedNewsClick}>
                                    <PlusRegularSmallIcon/>
                                    <div className="featured-news-selector-title-add-text">Add</div>
                                </div>
                            </>}
                            
                        </div>
                        <div className="featured-news-selector-subtitle">
                           Any remaining space will be filled with most recent news items. 
                        </div>
                    </div>
                    {!isFeaturedNewsActive ? (
                        <div 
                            className="featured-news-selector-add-news-container"
                            onClick={handleFeaturedNewsClick}
                        >
                            <div className="featured-news-selector-add-news-icon">
                                <PlusRegularIcon />
                            </div>
                            <div className="featured-news-selector-add-news-text">Add featured news</div>
                        </div>
                    ) : (
                        <div className="featured-news-selector-active-container">
                            <div className="featured-news-selector-active-container-heading">
                                <LabelledField label="Sub-heading: Featured news feed">
                                    <TextField
                                        onChange={onChangeFeaturedNewsHeading}
                                        id="featured_news_heading"
                                        defaultValue={featuredNewsHeading}
                                        className="news-highlights-text-field"
                                    />
                                </LabelledField>
                            </div>
                            <div className="featured-news-selector-active-container-pages">
                                <Stack direction="column">
                                    {featuredPagesList && featuredPagesList.map((item, index) => (
                                        <FeaturedPageItem
                                            key={`featured-page-${item?.guid}-${index}`}
                                            item={item}
                                            index={index}
                                            list={featuredPagesList}
                                            token={token}
                                            sortableMethods={sortableMethods}
                                            updateItems={updateItems}
                                            removeItem={removeItem}
                                            onAddToFeaturedPages={handleAddToFeaturedPages}
                                        />
                                    ))}
                                </Stack>
                            </div>
                        </div>
                    )}
                </div>
                {/* Shared dialog for adding new items from the "Add" button */}
                {addDialogOpen && (
                    <NewsItemSelectDialog
                        open={addDialogOpen}
                        onClose={() => setAddDialogOpen(false)}
                        onAddToFeaturedPages={handleAddToFeaturedPages}
                    />
                )}
            </div>
        </div>
    );
};

NewsHighlightsComponentEditor.displayName = "NewsHighlightsComponentEditor";
export { NewsHighlightsComponentEditor };

